import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaSyncAlt } from 'react-icons/fa';
import { useTheme } from '../../context/ThemeContext';
import {
  generatePassword, passwordEntropy, passwordStrength, MIN_LENGTH, MAX_LENGTH,
} from '../../lib/password';
import CopyButton from './CopyButton';
import { buttonClass, labelClass, mutedClass, panelClass } from '../uiStyles';

const TOGGLES = ['lower', 'upper', 'digits', 'symbols', 'avoidAmbiguous'];

const STRENGTH_STYLE = {
  weak: { bar: 'bg-red-500', width: 'w-1/4' },
  fair: { bar: 'bg-amber-500', width: 'w-2/4' },
  good: { bar: 'bg-lime-500', width: 'w-3/4' },
  strong: { bar: 'bg-emerald-500', width: 'w-full' },
};

const PasswordGenerator = () => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const [options, setOptions] = useState({
    length: 16, lower: true, upper: true, digits: true, symbols: true, avoidAmbiguous: false,
  });
  const [password, setPassword] = useState('');
  const [unsupported, setUnsupported] = useState(false);

  const regenerate = useCallback(() => {
    try {
      setPassword(generatePassword(options));
      setUnsupported(false);
    } catch {
      // Only fires on browsers without Web Crypto; a guessable password would
      // be worse than none at all.
      setPassword('');
      setUnsupported(true);
    }
  }, [options]);

  useEffect(() => { regenerate(); }, [regenerate]);

  const toggle = (key) => setOptions((current) => ({ ...current, [key]: !current[key] }));

  const noSets = !options.lower && !options.upper && !options.digits && !options.symbols;
  const entropy = passwordEntropy(options.length, options);
  const strength = passwordStrength(entropy);
  const style = STRENGTH_STYLE[strength];

  return (
    <div className="space-y-4">
      <div className={panelClass(isDarkMode)}>
        <p className={`text-xs mb-2 ${mutedClass(isDarkMode)}`}>{t('useful.password.generated')}</p>
        <p className={`font-mono text-lg md:text-xl break-all ${isDarkMode ? 'text-primary' : 'text-green-600'}`}>
          {password || (unsupported ? t('useful.password.unsupported') : t('useful.password.noSets'))}
        </p>
      </div>

      <div>
        <div className="flex items-center justify-between">
          <label className={labelClass(isDarkMode)} htmlFor="password-length">
            {t('useful.password.length')}
          </label>
          <span className={`text-sm font-semibold ${isDarkMode ? 'text-light' : 'text-gray-700'}`}>
            {options.length}
          </span>
        </div>
        <input
          id="password-length"
          type="range"
          min={MIN_LENGTH}
          max={MAX_LENGTH}
          value={options.length}
          onChange={(event) => setOptions((current) => ({ ...current, length: Number(event.target.value) }))}
          className={`w-full accent-current ${isDarkMode ? 'text-primary' : 'text-green-600'}`}
        />
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
        {TOGGLES.map((key) => (
          <label
            key={key}
            className={`flex items-center gap-2 px-3 py-2 rounded-lg cursor-pointer text-sm ${isDarkMode
              ? 'bg-dark/50 text-light/80 border border-primary/10'
              : 'bg-gray-50 text-gray-700 border border-gray-100'}`}
          >
            <input
              type="checkbox"
              checked={Boolean(options[key])}
              onChange={() => toggle(key)}
              className={isDarkMode ? 'accent-[#00cc7e]' : 'accent-green-600'}
            />
            {t(`useful.password.${key}`)}
          </label>
        ))}
      </div>

      <div>
        <div className="flex items-center justify-between mb-1">
          <span className={`text-xs ${mutedClass(isDarkMode)}`}>
            {t('useful.password.entropy', { bits: noSets ? 0 : entropy })}
          </span>
          <span className={`text-xs font-semibold ${isDarkMode ? 'text-light/80' : 'text-gray-600'}`}>
            {t(`useful.password.strength.${noSets ? 'weak' : strength}`)}
          </span>
        </div>
        <div className={`h-2 rounded-full overflow-hidden ${isDarkMode ? 'bg-dark' : 'bg-gray-200'}`}>
          <div className={`h-full transition-all ${noSets ? 'w-0' : `${style.bar} ${style.width}`}`} />
        </div>
      </div>

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={regenerate}
          disabled={noSets}
          className={`${buttonClass(isDarkMode, 'primary')} inline-flex items-center gap-2`}
        >
          <FaSyncAlt /> {t('useful.password.generate')}
        </button>
        <CopyButton value={password} />
      </div>
    </div>
  );
};

export default PasswordGenerator;
